"use client";

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { RotateCcw, Mail, List } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const heroPattern = '/assets/marketing/hero-pattern-960w.webp';

const sections = [
  { id: 'credit-packages', label: 'Credit Packages & Purchases' },
  { id: 'eu-consumer-rights', label: 'EU Consumer Rights' },
  { id: 'refund-eligibility', label: 'Refund Eligibility' },
  { id: 'non-refundable', label: 'Non-Refundable Items' },
  { id: 'how-to-request', label: 'How to Request a Refund' },
  { id: 'chargebacks', label: 'Chargebacks & Disputes' },
  { id: 'service-interruptions', label: 'Service Interruptions' },
  { id: 'policy-modifications', label: 'Policy Modifications' },
  { id: 'contact', label: 'Contact Us' },
];

export default function RefundPolicyClient() {
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-100px 0px -80% 0px', threshold: 0 }
    );
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
    }
  };

  return (
    <main className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative overflow-hidden border-b" style={{ backgroundColor: '#E9FBFA' }}>
        <div className="hidden md:block absolute inset-0 opacity-60" style={{ backgroundImage: `url(${heroPattern})`, backgroundSize: 'cover' }} />
        <div className="container mx-auto relative z-10 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-[#195cc8] text-sm font-medium mb-6">
              <RotateCcw className="w-4 h-4" />
              Refund Policy
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Refund Policy</h1>
            <p className="text-lg text-muted-foreground">Last updated: October 27, 2025</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto py-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-[250px_1fr] gap-8">
            {/* TOC Sidebar */}
            <nav className="hidden lg:block">
              <div className="sticky top-32 border rounded-lg p-4 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-foreground">
                  <List className="h-4 w-4" />
                  <span>On this page</span>
                </div>
                <ul className="space-y-1 text-sm">
                  {sections.map(({ id, label }) => (
                    <li key={id}>
                      <a
                        href={`#${id}`}
                        onClick={(e) => scrollTo(e, id)}
                        className={cn(
                          'block py-1 px-2 rounded transition-colors hover:bg-muted',
                          activeId === id
                            ? 'text-primary font-medium border-l-2 border-primary pl-3'
                            : 'text-muted-foreground hover:text-foreground'
                        )}
                      >
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </nav>

            {/* Content */}
            <div className="space-y-8">
              {/* Intro */}
              <div className="mb-2 space-y-4 text-muted-foreground">
                <p>
                  At Hirebee.ai, we are committed to providing exceptional AI-powered assessment services and ensuring customer satisfaction.
                  This Refund Policy outlines the terms and conditions under which refunds may be requested and processed for our credit-based assessment platform.
                </p>
                <p>
                  Our payment processing and order fulfillment is handled by Paddle.com Market Limited (UK) and Paddle.com Inc. (US),
                  our authorized reseller and Merchant of Record. Paddle is the seller of record for all transactions and handles payment processing,
                  tax collection, and refund processing on our behalf.
                </p>
                <p>
                  By purchasing credits for our services, you agree to the terms outlined in this policy. We encourage you to read this policy
                  carefully and contact us at <a href="mailto:hello@hirebee.ai" className="text-primary hover:underline">hello@hirebee.ai</a> if
                  you have any questions before making a purchase.
                </p>
              </div>

              <Card id="credit-packages">
                <CardHeader>
                  <CardTitle>1. Credit Packages &amp; Purchases</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-muted-foreground">
                  <p>Hirebee.ai operates on a one-time purchase, credit-based system where credits are used to conduct AI assessments. When you purchase a credit package:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Each purchase is a one-time transaction with no recurring billing</li>
                    <li>Unused credits are eligible for refund within 30 days of purchase</li>
                    <li>Credits that have been used to conduct assessments are non-refundable</li>
                    <li>Promotional credits, bonus credits, or credits received as part of a special offer are non-refundable</li>
                    <li>Refunds will be issued to the original payment method used for the purchase</li>
                  </ul>
                  <p>
                    To be eligible for a credit refund, you must request the refund within 30 days of the original purchase date
                    (or 14 days for EU/EEA customers under consumer protection law) and must not have used the credits for any assessments.
                  </p>
                  <p>
                    <strong className="text-foreground">Express Consent:</strong> By completing your purchase, you expressly consent to the immediate provision of digital
                    services and acknowledge that using credits waives your right of withdrawal for the consumed portion of your purchase.
                  </p>
                </CardContent>
              </Card>

              <Card id="eu-consumer-rights">
                <CardHeader>
                  <CardTitle>1a. EU Consumer Rights &amp; Right of Withdrawal</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-muted-foreground">
                  <p>For customers in the European Union (EU) and European Economic Area (EEA):</p>
                  <p>
                    You have the right to withdraw from this purchase within 14 days without giving any reason, in accordance with
                    EU Consumer Rights Directive 2011/83/EU. The withdrawal period will expire 14 days from the date of purchase.
                  </p>
                  <p><strong className="text-foreground">IMPORTANT:</strong> By purchasing and using credits to conduct assessments, you expressly agree that:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>The performance of digital services begins immediately upon credit purchase</li>
                    <li>You acknowledge that once credits are used to generate assessments, you lose your right of withdrawal for those consumed credits</li>
                    <li>Unused credits remain eligible for refund within the 14-day EU withdrawal period or our extended 30-day refund window, whichever is more favorable to you</li>
                  </ul>
                  <p>
                    To exercise your right of withdrawal, you must inform us of your decision by contacting{' '}
                    <a href="mailto:hello@hirebee.ai" className="text-primary hover:underline">hello@hirebee.ai</a> before using any credits.
                  </p>
                </CardContent>
              </Card>

              <Card id="refund-eligibility">
                <CardHeader>
                  <CardTitle>2. Refund Eligibility Criteria</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-muted-foreground">
                  <p>Refunds may be granted under the following circumstances:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong className="text-foreground">Unused Services:</strong> Credits that have not been used for any assessments within 30 days of purchase</li>
                    <li><strong className="text-foreground">Technical Issues:</strong> Significant technical problems preventing you from using our services, provided you have contacted support and we were unable to resolve the issue</li>
                    <li><strong className="text-foreground">Duplicate Charges:</strong> If you were accidentally charged twice for the same purchase</li>
                    <li><strong className="text-foreground">Unauthorized Transactions:</strong> Charges made without your authorization, subject to verification</li>
                    <li><strong className="text-foreground">Service Defects:</strong> If our AI assessment service fails to perform as described and we cannot provide a suitable resolution</li>
                  </ul>
                  <p><strong className="text-foreground">Geographic Considerations:</strong></p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong className="text-foreground">EU/EEA customers:</strong> 14-day mandatory withdrawal right applies (see Section 1a)</li>
                    <li><strong className="text-foreground">US customers:</strong> 30-day refund window for unused credits</li>
                    <li><strong className="text-foreground">Other regions:</strong> 30-day refund window for unused credits</li>
                    <li>The most favorable terms to the customer will apply in all cases</li>
                  </ul>
                </CardContent>
              </Card>

              <Card id="non-refundable">
                <CardHeader>
                  <CardTitle>3. Non-Refundable Items</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-muted-foreground">
                  <p>The following purchases are not eligible for refunds:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Credits that have been used to conduct assessments</li>
                    <li>Completed assessment sessions or reports</li>
                    <li>Custom development, consulting services, or enterprise solutions</li>
                    <li>Promotional credits, bonus credits, or credits received through referral programs</li>
                    <li>Credits purchased more than 30 days ago</li>
                    <li>Credits that have expired due to inactivity or account terms</li>
                  </ul>
                </CardContent>
              </Card>

              <Card id="how-to-request">
                <CardHeader>
                  <CardTitle>4. How to Request a Refund</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-muted-foreground">
                  <p>To request a refund, please follow these steps:</p>
                  <ol className="list-decimal pl-6 space-y-2">
                    <li>Contact our support team at <a href="mailto:hello@hirebee.ai" className="text-primary hover:underline">hello@hirebee.ai</a> with the subject line &ldquo;Refund Request&rdquo;</li>
                    <li>Include your account email, purchase date, transaction ID (if available), and reason for the refund request</li>
                    <li>Provide any relevant documentation or screenshots that support your refund claim</li>
                    <li>Our team will review your request within 3-5 business days</li>
                    <li>If approved, refunds will be processed within 7-10 business days to the original payment method</li>
                  </ol>
                  <p>
                    Please note that it may take an additional 5-10 business days for the refund to appear in your account,
                    depending on your financial institution&apos;s processing times.
                  </p>
                  <p>
                    <strong className="text-foreground">Note about Paddle:</strong> All refunds are processed through Paddle, our payment processor and Merchant of Record.
                    When approved, Paddle will handle the refund transaction directly to your original payment method.
                  </p>
                </CardContent>
              </Card>

              <Card id="chargebacks">
                <CardHeader>
                  <CardTitle>5. Chargebacks &amp; Payment Disputes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-muted-foreground">
                  <p>We strongly encourage you to contact us directly before initiating a chargeback with your bank or credit card company. Chargebacks can result in:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Immediate suspension of your account and services</li>
                    <li>Loss of all unused credits without refund</li>
                    <li>Inability to use Hirebee.ai services in the future</li>
                    <li>Additional fees to cover chargeback processing costs</li>
                  </ul>
                  <p>
                    We are committed to resolving all billing disputes fairly and promptly. Please give us the opportunity to address
                    your concerns before pursuing a chargeback.
                  </p>
                </CardContent>
              </Card>

              <Card id="service-interruptions">
                <CardHeader>
                  <CardTitle>6. Service Interruptions &amp; Force Majeure</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-muted-foreground">
                  <p>
                    In the event of service interruptions due to circumstances beyond our reasonable control (including but not limited to
                    natural disasters, acts of government, network failures, or third-party service outages):
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>We will not provide refunds for temporary service interruptions</li>
                    <li>We may offer credit extensions or bonus credits for significant prolonged outages</li>
                    <li>We will communicate transparently about any issues and expected resolution times</li>
                    <li>For extended outages exceeding 72 hours, please contact support to discuss potential remedies</li>
                  </ul>
                </CardContent>
              </Card>

              <Card id="policy-modifications">
                <CardHeader>
                  <CardTitle>7. Modifications to This Policy</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-muted-foreground">
                  <p>
                    Hirebee.ai reserves the right to modify this Refund Policy at any time. Changes will be effective immediately upon
                    posting to our website. Material changes will be communicated via:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Email notification to registered users (if we have your email address)</li>
                    <li>Prominent notice on our website and platform</li>
                    <li>Updated &ldquo;Last updated&rdquo; date at the top of this policy</li>
                  </ul>
                  <p>
                    Your continued use of Hirebee.ai services after any modifications to this policy constitutes acceptance of the updated terms.
                    We encourage you to review this policy periodically.
                  </p>
                </CardContent>
              </Card>

              <Card id="contact" className="border-primary/20 bg-primary/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-primary" />
                    8. Contact Us
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6 text-muted-foreground">
                  <p>
                    If you have any questions about our Refund Policy, wish to request a refund, or need assistance with billing issues,
                    please don&apos;t hesitate to contact us.
                  </p>
                  <div className="bg-background rounded-lg p-5 border space-y-3">
                    <h4 className="font-semibold text-foreground">DeepRedMedia LLC (DBA Hirebee)</h4>
                    <ul className="space-y-2 text-sm">
                      <li><strong className="text-foreground">Email:</strong> <a href="mailto:hello@hirebee.ai" className="text-primary hover:underline">hello@hirebee.ai</a></li>
                      <li><strong className="text-foreground">Phone:</strong> <a href="tel:+14708099036" className="text-primary hover:underline">+1 (470) 809-9036</a></li>
                      <li><strong className="text-foreground">Address:</strong> 75 5th St NW, Atlanta, GA 30308, United States</li>
                    </ul>
                    <p className="text-sm">
                      Our support team is available Monday through Friday, 9:00 AM - 6:00 PM EST.
                      We strive to respond to all inquiries within 24-48 hours during business days.
                    </p>
                  </div>
                  <div className="bg-background rounded-lg p-5 border space-y-3">
                    <h4 className="font-semibold text-foreground">Paddle (Payment Processor)</h4>
                    <p className="text-sm">For refund requests related to Paddle transactions, you may also contact Paddle directly:</p>
                    <ul className="space-y-2 text-sm">
                      <li><strong className="text-foreground">Support:</strong> <a href="https://www.paddle.com/support" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">paddle.com/support</a></li>
                    </ul>
                    <p className="text-sm"><strong className="text-foreground">For EU customers:</strong><br />Paddle.com Market Limited, Judd House, 18-29 Mora Street, London, EC1V 8BT, United Kingdom</p>
                    <p className="text-sm">However, we encourage you to contact us first at hello@hirebee.ai for faster resolution.</p>
                  </div>
                  <Link
                    to="/contacts"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Contact Us
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
